import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Modal, IconButton, Button, TextField } from '@mui/material';
import { IconCross } from '../../utils/icons';
import FormKeyPoints from '../../pages/pro/createEditProfile/formFragments/keyPoints';
import { IKeyPoint } from '../../pages/pro/createEditProfile/formFragments/constants';
import axiosInstant from '../../api';
import { DotLottleLoader } from '../Loader';

interface IProjectKeyPoint extends IKeyPoint {
  id?: number;
  order?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  expert: any;
  project: any;
  onUpdate: () => void;
}

export const ExpertKeyPointsModal: React.FC<Props> = ({
  open,
  onClose,
  expert,
  project,
  onUpdate,
}) => {
  const [keyPoints, setKeyPoints] = useState<IProjectKeyPoint[]>([]);
  const [localKeyPoints, setLocalKeyPoints] = useState<IProjectKeyPoint[]>([]);
  const [projectBio, setProjectBio] = useState<string>('');
  const [originalBio, setOriginalBio] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const formKeyPointsRef = useRef<{ mergePendingItems: () => IKeyPoint[] }>(null);

  const brandId = project?.brands?.find((b: any) => b.rmn !== true)?.id;

  useEffect(() => {
    if (open && expert?.userid && project?.id && brandId) {
      fetchExpertKeyPoints();
      fetchExpertBio();
    }
  }, [open, expert?.userid, project?.id, brandId]);

  const fetchExpertKeyPoints = async () => {
    if (!expert?.userid || !project?.id || !brandId) return;

    try {
      setLoading(true);
      const response = await axiosInstant.get(
        `/brands/${brandId}/projects/${project.id}/pros/${expert.userid}/expert-points`
      );

      const projectPoints = response.data.map((point: any) => ({
        keyPoint: point.key_point,
        id: point.id,
        order: point.order,
      }));

      setKeyPoints(projectPoints);
      setLocalKeyPoints(projectPoints);
    } catch (error) {
      console.error('Error fetching expert key points:', error);
      setKeyPoints([]);
      setLocalKeyPoints([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpertBio = async () => {
    if (!expert?.userid || !project?.id || !brandId) return;

    try {
      const response = await axiosInstant.get(
        `/brands/${brandId}/projects/${project.id}/pros/${expert.userid}/expert-bio`
      );
      const bio = response.data?.project_bio || '';
      setProjectBio(bio);
      setOriginalBio(bio);
    } catch (error) {
      console.error('Error fetching expert bio:', error);
      setProjectBio('');
      setOriginalBio('');
    }
  };

  const updateLocalKeyPoints = (newKeyPoints: IProjectKeyPoint[]) => {
    setLocalKeyPoints(newKeyPoints);
  };

  const handleSave = async () => {
    if (!expert?.userid || !project?.id || !brandId) return;

    try {
      setSaving(true);

      // Save bio if changed
      if (projectBio !== originalBio) {
        await axiosInstant.put(
          `/brands/${brandId}/projects/${project.id}/pros/${expert.userid}/expert-bio`,
          { project_bio: projectBio }
        );
      }

      // Merge pending items before saving
      const mergedKeyPoints = formKeyPointsRef.current?.mergePendingItems() || localKeyPoints;
      const mergedProjectKeyPoints: IProjectKeyPoint[] = mergedKeyPoints.map((kp, index) => {
        // Preserve existing IDs if they exist (match by keyPoint text)
        const existing = localKeyPoints.find(lkp => lkp.keyPoint === kp.keyPoint);
        return {
          keyPoint: kp.keyPoint,
          id: existing?.id,
          order: existing?.order ?? index
        };
      });
      setLocalKeyPoints(mergedProjectKeyPoints);

      const existingProjectPoints = keyPoints.filter(p => p.id !== undefined);
      const newPoints = mergedProjectKeyPoints.map(p => ({ keyPoint: p.keyPoint }));

      const pointsToDelete = existingProjectPoints.filter(
        existing => !newPoints.some(newPoint => newPoint.keyPoint === existing.keyPoint)
      );

      const pointsToAdd = newPoints.filter(
        newPoint => !existingProjectPoints.some(existing => existing.keyPoint === newPoint.keyPoint)
      );

      for (const point of pointsToDelete) {
        if (point.id) {
          await axiosInstant.delete(
            `/brands/${brandId}/projects/${project.id}/pros/${expert.userid}/expert-points/${point.id}`
          );
        }
      }

      for (const point of pointsToAdd) {
        await axiosInstant.post(
          `/brands/${brandId}/projects/${project.id}/pros/${expert.userid}/expert-points`,
          { key_point: point.keyPoint }
        );
      }

      const pointsWithIds = mergedProjectKeyPoints.filter(point => point.id !== undefined);
      if (pointsWithIds.length > 0) {
        const reorderData = pointsWithIds.map((point, index) => ({
          id: point.id,
          order: index,
        }));

        await axiosInstant.post(
          `/brands/${brandId}/projects/${project.id}/pros/${expert.userid}/expert-points/reorder`,
          { points: reorderData }
        );
      }

      await fetchExpertKeyPoints();
      await fetchExpertBio();
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating expert key points:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalKeyPoints(keyPoints);
    setProjectBio(originalBio);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="expert-key-points-modal"
      aria-describedby="expert-key-points-modal-description"
    >
      <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-surface rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <Box className="flex items-center justify-between mb-4">
          <Typography variant="h6">
          Project Bio / Key Points for Expert
          </Typography>
          <IconButton onClick={onClose} size="small">
            <IconCross width={20} height={20} />
          </IconButton>
        </Box>

        <Typography variant="body2" color="var(--text-2)" style={{ marginBottom: '20px' }}>
          Add project specific key points and bio that will replace the expert's defaults for the project only.
        </Typography>

        {loading ? (
          <Box className="flex justify-center items-center h-32">
            <DotLottleLoader sx={{ width: 200, height: 200 }} />
          </Box>
        ) : (
          <>
            <Box className="mb-6">
              <Typography variant="body2" className="font-medium" style={{ marginBottom: '8px', color: 'var(--text)' }}>
                Bio
              </Typography>
              <TextField
                multiline
                minRows={3}
                maxRows={8}
                fullWidth
                placeholder="Add biography..."
                value={projectBio}
                onChange={(e) => setProjectBio(e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--surface-2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    '& fieldset': {
                      borderColor: 'var(--border)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--accent)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--accent)',
                    },
                  },
                }}
              />
            </Box>

            <FormKeyPoints
              ref={formKeyPointsRef}
              keyPoints={localKeyPoints}
              updateKeyPoints={updateLocalKeyPoints}
            />
            <Box className="flex justify-end gap-3 mt-6 pt-4">
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ backgroundColor: 'var(--accent)', '&:hover': { backgroundColor: '#8B6B3A' } }}
              >
                Save
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};
